import { Meeting } from '@consigli/types';
import { useMemo } from 'react';

import { toMsTimestamp } from './format-meeting-datetime';

const byDateDesc = (a: Meeting, b: Meeting): number =>
  toMsTimestamp(b.scheduledStartTime) - toMsTimestamp(a.scheduledStartTime);

export interface MeetingSeries {
  seriesId: string;
  seriesIds: string[];
  title: string;
  meetings: Meeting[];
  nextMeeting: Meeting | null;
  lastMeeting: Meeting | null;
}

export interface GroupedMeetings {
  series: MeetingSeries[];
  standalone: Meeting[];
}

export const useGroupedMeetings = (meetings: Meeting[]): GroupedMeetings => {
  return useMemo(() => {
    const seriesMap = new Map<string, MeetingSeries>();
    const standalone: Meeting[] = [];

    meetings.forEach((meeting) => {
      if (meeting.recurringSeriesId) {
        const groupKey = (meeting.recurringSeriesTitle ?? meeting.recurringSeriesId) as string;

        if (!seriesMap.has(groupKey)) {
          seriesMap.set(groupKey, {
            seriesId: meeting.recurringSeriesId,
            seriesIds: [],
            title: (meeting.recurringSeriesTitle ||
              meeting.recurringSeries?.title ||
              meeting.title) as string,
            meetings: [],
            nextMeeting: null,
            lastMeeting: null,
          });
        }

        const group = seriesMap.get(groupKey)!;
        group.meetings.push(meeting);

        if (!group.seriesIds.includes(meeting.recurringSeriesId)) {
          group.seriesIds.push(meeting.recurringSeriesId);
        }
      } else {
        standalone.push(meeting);
      }
    });

    const now = new Date();
    seriesMap.forEach((series) => {
      series.meetings.sort(byDateDesc);

      series.seriesId = series.meetings[0]?.recurringSeriesId ?? series.seriesId;

      const futureMeetings = series.meetings.filter(
        (m) => toMsTimestamp(m.scheduledStartTime) >= now.getTime(),
      );
      series.nextMeeting = futureMeetings[futureMeetings.length - 1] || null;
      series.lastMeeting = series.meetings[0] || null;
    });

    standalone.sort(byDateDesc);

    const sortedSeries = Array.from(seriesMap.values()).sort(
      (a, b) =>
        (b.lastMeeting ? toMsTimestamp(b.lastMeeting.scheduledStartTime) : 0) -
        (a.lastMeeting ? toMsTimestamp(a.lastMeeting.scheduledStartTime) : 0),
    );

    return {
      series: sortedSeries,
      standalone,
    };
  }, [meetings]);
};
